import React, { memo, useState } from 'react'
import styled from 'styled-components'
import { Button } from 'primereact/button'

import { WidgetLayout } from '../widget-layout'
import { Table, TableBody, TableBodyCell, TableRow } from '../table'
import Text from '../../ui/Text'
import useShortDatesNames from '../shortDatesNames'
import { GreenChargingBadge } from '../green-charging-badge'
import { isAdminDomain, isUserappDomain } from '../currentDomain'
import { CapiScheduleDialog } from '../../common/dialogs/capi-schedule-dialog'
import {
    ChargePoint,
    Schedule,
    useScheduleCopyScheduleFromChargePointMutation,
    useScheduleDeleteScheduleMutation,
    useScheduleGetByChargePointQuery,
} from '../../common/redux/api/web-api'
import { notifySuccess, notifyError } from '../../common/features/notificationsManager/Notify'
import useText from '../../common/hooks/useText'
import { useAdaptConnectorNaming } from '../adaptConnectorNaming'

const FlexRow = styled.div`
    display: flex;
    justify-content: space-between;
    align-items: center;
`

const Row = styled.div`
    padding-bottom: 2px;
`

const NameField = styled.span`
    font-weight: var(--font-weight-bold);
    color: ${({ theme }) => theme.brandColors.CA_Blue};

    &:hover {
        cursor: pointer;
    }
`

const IconContainer = styled.div`
    display: flex;
    gap: 2px;
`

const Icon = styled.span`
    transition: 0.2s ease all;
    color: ${({ theme }) => theme.brandColors.CA_Blue};

    &:hover {
        cursor: pointer;
        color: ${({ theme }) => theme.brandColors.CA_Mid_Blue};
    }
`

const Title = styled.span`
    font-weight: var(--font-weight-bold);
`

const SText = styled.span`
    font-weight: var(--font-weight-light);
`

const SDisabledOverlay = styled.div`
    position: absolute;
    top: 0;
    left: 0;
    bottom: 0;
    right: 0;
    z-index: 1;
    opacity: 50%;
    background-color: #d4d4d4;

    &.transparent {
        background-color: transparent;
    }
`

const GreenChargingContainer = styled.div`
    margin-top: 12px;
`

export type ChargePointCapiScheduledChargingWidgetProps = {
    chargePoint: ChargePoint
    isGreenChargingEnabled?: boolean
}

export const ChargePointCapiScheduledChargingWidget = memo(function ChargePointCapiScheduledChargingWidget({
    chargePoint,
    isGreenChargingEnabled,
}: ChargePointCapiScheduledChargingWidgetProps): React.ReactElement {
    const text = useText()
    const [scheduleDialogIsOpen, setScheduleDialogIsOpen] = useState(false)
    const [selectedSchedule, setSelectedSchedule] = useState<Schedule | undefined>(undefined)

    const shortDatesNames = useShortDatesNames()
    const adaptConnectorNaming = useAdaptConnectorNaming()

    const {
        data: schedules,
        refetch: refreshSchedule,
        isLoading: isLoadingSchedules,
    } = useScheduleGetByChargePointQuery({
        chargePointId: chargePoint.id,
    })

    const [removeScheduleRequest] = useScheduleDeleteScheduleMutation()
    const removeSchedule = async (scheduleId: number) => {
        try {
            await removeScheduleRequest({
                scheduleId,
            }).unwrap()

            notifySuccess(text('msgSchedulesRemoved'))
        } catch (exception) {
            notifyError(text('msgScheduleFailedToRemove'))
        }
    }

    const [copySchedulesRequest] = useScheduleCopyScheduleFromChargePointMutation()
    const copySchedules = async () => {
        try {
            await copySchedulesRequest({
                chargePointId: chargePoint.id,
            }).unwrap()

            notifySuccess(text('msgSchedulesCopied'))
        } catch (exception) {
            notifyError(text('msgScheduleFailedToRemove'))
        }
    }

    return (
        <>
            <WidgetLayout
                title={text('lblSchedulingWidget')}
                data-testid="scheduleTitle"
                loadingStatus={isLoadingSchedules}
                actionButtons={
                    <>
                        {isAdminDomain && (
                            <Button
                                type="button"
                                onClick={() => copySchedules()}
                                data-testid="copySchedules"
                                label={text('lblCopyToAllChargersLink')}
                                icon="pi pi-copy"
                                disabled={!schedules || schedules.length === 0}
                            />
                        )}
                        {((!chargePoint.ownerReadOnly && isUserappDomain) || isAdminDomain) && (
                            <Button
                                type="button"
                                onClick={() => {
                                    setSelectedSchedule(undefined)
                                    setScheduleDialogIsOpen(true)
                                }}
                                data-testid="addSchedule"
                                label={text('lblCreateScheduleLink')}
                                icon="pi pi-calendar-plus"
                            />
                        )}
                    </>
                }
            >
                {isLoadingSchedules ? (
                    <div className="shimmer-container shimmer-medium margin-bottom-10"></div>
                ) : (
                    <>
                        {schedules && (
                            <Table data-testid="schedules-table">
                                <TableBody>
                                    {schedules.map((schedule, index) => (
                                        <TableRow
                                            key={index}
                                            onClick={() =>
                                                isAdminDomain
                                                    ? () => {
                                                          setSelectedSchedule(schedule)
                                                          setScheduleDialogIsOpen(true)
                                                      }
                                                    : null
                                            }
                                        >
                                            <TableBodyCell>
                                                <FlexRow>
                                                    <NameField
                                                        onClick={
                                                            (!chargePoint.ownerReadOnly && isUserappDomain) ||
                                                            isAdminDomain
                                                                ? () => {
                                                                      setSelectedSchedule(schedule)
                                                                      setScheduleDialogIsOpen(true)
                                                                  }
                                                                : () => null
                                                        }
                                                        data-testid={'schedule-' + schedule.id}
                                                    >
                                                        {schedule.name}
                                                        {!schedule.active && (
                                                            <span data-testid="inactiveText">
                                                                <Text code="lblInactiveSchedule" />
                                                            </span>
                                                        )}
                                                    </NameField>
                                                    {((!chargePoint.ownerReadOnly && isUserappDomain) ||
                                                        isAdminDomain) && (
                                                        <IconContainer>
                                                            <Icon
                                                                className="fa fa-trash aweFontSize"
                                                                onClick={() => {
                                                                    if (schedule.id) removeSchedule(schedule.id)
                                                                }}
                                                                data-testid={'scheduleRemove-' + schedule.id}
                                                            ></Icon>
                                                        </IconContainer>
                                                    )}
                                                </FlexRow>
                                                <Row>
                                                    <Title data-testid={'scheduleAvg-' + schedule.id}>
                                                        <Text code="lblChargeActive" />
                                                    </Title>{' '}
                                                    <SText data-testid={'scheduleAvgVal-' + schedule.id}>
                                                        {schedule.weekdaySummary &&
                                                            shortDatesNames(schedule.weekdaySummary)}
                                                    </SText>
                                                </Row>
                                                {chargePoint.connectors!.map((c, j) => {
                                                    if (schedule.connectorIds?.includes(c.connectorId!)) {
                                                        return (
                                                            <Row key={j}>
                                                                <Title data-testid={'scheduleConn-' + c.connectorId}>
                                                                    {adaptConnectorNaming(c.name)}
                                                                </Title>{' '}
                                                                <SText data-testid={'scheduleConnVal-' + c.connectorId}>
                                                                    {schedule.summary}
                                                                </SText>
                                                            </Row>
                                                        )
                                                    } else {
                                                        return (
                                                            <Row key={j}>
                                                                <Title data-testid={'scheduleConn-' + c.connectorId}>
                                                                    {adaptConnectorNaming(c.name)}
                                                                </Title>{' '}
                                                                <SText data-testid={'scheduleConnVal-' + c.connectorId}>
                                                                    <Text code="lblUnaffectedConnector" />
                                                                </SText>
                                                            </Row>
                                                        )
                                                    }
                                                })}
                                            </TableBodyCell>
                                        </TableRow>
                                    ))}
                                </TableBody>
                            </Table>
                        )}
                        {isGreenChargingEnabled && (
                            <GreenChargingContainer>
                                <GreenChargingBadge />
                                <SDisabledOverlay></SDisabledOverlay>
                            </GreenChargingContainer>
                        )}
                    </>
                )}
            </WidgetLayout>
            <CapiScheduleDialog
                visible={scheduleDialogIsOpen}
                onHide={() => setScheduleDialogIsOpen(false)}
                onSave={() => void refreshSchedule()}
                chargePoint={chargePoint}
                schedule={selectedSchedule}
            />
        </>
    )
})
